import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { Print as PrintIcon, Save as SaveIcon } from '@mui/icons-material';
import axios from 'axios';
import useSWR from 'swr';
import { apiHost } from '../utils/apiHost';
import { TombolUlasanOtomatis, useStatusUlasan } from './UlasanGoogleAlat';
import { bangunSvgKartu } from '../struk/kartuUlasan';
import { alamatUlasanPendek } from '../utils/ulasanPendek';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const api = axios.create({ baseURL: getApiBaseUrl(), withCredentials: true });
const fetcher = (url) => api.get(url).then((r) => r.data);

/**
 * Kartu "Ulasan Google" di Konfigurasi (per cabang): tempat tautan ulasan diisi. Tautan yang sama dipakai di QR struk (sakelar di menu
 * Struk), layar pelanggan, menu QR, dan kartu QR cetak. Diisi dari tautan Google Maps atau cari otomatis (bila kunci Google Places terpasang).
 */
export const UlasanGoogleCard = ({ cabangUuid, onCetakKartu = null }) => {
  const { data, isLoading, mutate } = useSWR(
    cabangUuid ? `/getkonfigurasicabang?cabangUuid=${cabangUuid}` : null,
    (url) => api.get(url).then((r) => r.data).catch((e) => {
      if (e.response?.status === 404) return { status: true, data: null }; // konfigurasi belum dibuat: dibuat saat menyimpan
      throw e;
    }),
  );
  const { data: cabangData } = useSWR(cabangUuid ? `/cabang/${cabangUuid}` : null, fetcher);
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const { bolehAtur, memuat: memuatStatus } = useStatusUlasan();
  const tersimpan = data?.data?.ulasanGoogleUrl || ''; // hanya terkirim ke superadmin
  const aktif = !!data?.data?.ulasanAktif;
  const namaCabang = cabangData?.data?.namacabang || '';
  useEffect(() => { setUrl(tersimpan); setFeedback(null); }, [tersimpan, cabangUuid]);

  const bersih = url.trim();
  const salah = bersih !== '' && !/^https:\/\//i.test(bersih);
  // Pratinjau hanya untuk tautan sah dan tidak lebih panjang dari batas server (500); QR menolak teks terlalu panjang, jangan sampai merobohkan halaman.
  // QR di pratinjau memuat alamat pendek milik kita (seperti kartu cetak), bukan tautan Google; sebelum disimpan kodenya contoh.
  const kode = data?.data?.ulasanKode || '';
  const alamatQr = alamatUlasanPendek({ ulasanAktif: true, ulasanKode: kode }); // kosong bila kode belum ada
  const pratinjau = useMemo(() => {
    const ada = bolehAtur ? (bersih && !salah && bersih.length <= 500) : aktif;
    if (!ada) return '';
    const urlQr = alamatUlasanPendek({ ulasanAktif: true, ulasanKode: kode || 'contoh123' });
    try {
      return bangunSvgKartu({ lebar: 100, tinggi: 100, namaCabang, logoDataUrl: '', urlUlasan: urlQr });
    } catch (e) {
      return '';
    }
  }, [bolehAtur, aktif, bersih, salah, kode, namaCabang]);

  const simpan = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await api.put('/updatekonfigurasicabang', { cabangUuid, ulasanGoogleUrl: bersih });
      await mutate();
      setFeedback({ type: 'success', msg: bersih ? 'Tautan ulasan Google disimpan.' : 'Tautan ulasan Google dikosongkan.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.msg || err.response?.data?.message || 'Gagal menyimpan tautan ulasan.' });
    } finally {
      setSaving(false);
    }
  };

  if (!cabangUuid) return null;

  return (
    <Card sx={{ mb: 3 }} data-testid="kartu-ulasan-konfigurasi">
      <CardHeader
        title="Ulasan Google"
        subheader="Pelanggan memindai QR dan langsung masuk ke halaman tulis ulasan Google cabang ini. QR tampil di struk (sakelar di menu Struk), layar pelanggan, dan menu QR."
      />
      <CardContent>
        {(isLoading || memuatStatus) ? <CircularProgress size={24} /> : !bolehAtur ? (
          <>
            <Chip size="small" color={aktif ? 'success' : 'default'} label={aktif ? 'Tautan ulasan sudah diisi' : 'Tautan ulasan belum diisi'} sx={{ mb: 1.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Tautan ulasan Google hanya terlihat dan bisa diubah oleh superadmin. QR memuat alamat pendek milik kita yang meneruskan ke halaman ulasan.
              {!aktif && ' Minta superadmin mengisinya.'}
            </Typography>
            {pratinjau && (
              <Box
                data-testid="pratinjau-kartu-mini"
                sx={{ width: 200, mb: 1.5, boxShadow: 2, borderRadius: 1.5, overflow: 'hidden', lineHeight: 0, '& svg': { width: '100%', height: 'auto' } }}
                dangerouslySetInnerHTML={{ __html: pratinjau }}
              />
            )}
            <Button variant="outlined" startIcon={<PrintIcon />} disabled={!aktif || !onCetakKartu} onClick={() => onCetakKartu()}>
              Cetak kartu QR ulasan
            </Button>
          </>
        ) : (
          <>
            <TextField
              fullWidth
              size="small"
              label="Tautan ulasan Google"
              placeholder="https://search.google.com/local/writereview?placeid=..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={saving}
              error={salah}
              helperText={salah
                ? 'Tautan harus diawali https:// dan berasal dari Google.'
                : 'Ambil dari tautan Google Maps (tombol Bagikan) atau cari otomatis. Kosongkan untuk tanpa QR ulasan.'}
            />
            <TombolUlasanOtomatis cabangUuid={cabangUuid} namaCabang={namaCabang} disabled={saving} onPilih={(tautan) => setUrl(tautan)} />
            {alamatQr && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }} data-testid="alamat-qr">
                Alamat yang dimuat QR (diteruskan ke tautan di atas): <b>{alamatQr}</b>
              </Typography>
            )}
            {pratinjau && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Pratinjau kartu (ukuran dan logo diatur di tab Kartu QR cetak)</Typography>
                <Box
                  data-testid="pratinjau-kartu-mini"
                  sx={{ width: 200, boxShadow: 2, borderRadius: 1.5, overflow: 'hidden', lineHeight: 0, '& svg': { width: '100%', height: 'auto' } }}
                  dangerouslySetInnerHTML={{ __html: pratinjau }}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={simpan} disabled={saving || salah || bersih === tersimpan}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </Button>
              <Button variant="outlined" startIcon={<PrintIcon />} disabled={!(tersimpan || aktif) || saving || !onCetakKartu} onClick={() => onCetakKartu()}>
                Cetak kartu QR ulasan
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Kartu ukuran A6/A5/kotak atau ukuran sendiri, untuk standing acrylic atau stiker. Simpan tautan dulu agar tombol cetak aktif.
            </Typography>
            {feedback && <Alert severity={feedback.type} sx={{ mt: 1.5 }}>{feedback.msg}</Alert>}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default UlasanGoogleCard;
